import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { AppLayout } from '../components/AppLayout'
import { DiamondRow } from '../components/DiamondRow'
import { MediaModal } from '../components/MediaModal'
import { ResultsPane } from '../components/chat/ResultsPane'
import {
  chatApi, chatHistoryApi, sellerApi, adminApi,
  type ChatMessageHistory, type ChatAction, type ChatConfirm, type ChatStep,
  type ChatSessionMeta, type StoredChatMessage,
} from '../api/endpoints'
import { stoneKey } from '../lib/stoneKey'
import { useApp } from '../store'
import { watchAuthState } from '../lib/firebase'
import { ApiError, type Diamond, type SearchFilters } from '../api/types'
import { isSttAvailable, startStt, type SttSession } from '../lib/stt'

type Role = 'admin' | 'supplier' | 'buyer' | null

interface ChatMessage {
  id: string
  role: 'user' | 'assistant' | 'system'
  text: string
  thinking?: boolean
  /** Light agentic trail — what Gemma searched/checked (server-built). */
  steps?: ChatStep[]
  results?: Diamond[]
  /** Total catalog matches for the last search (results holds the top slice) —
   *  powers the "Browse all N" affordance in the wide-desktop results pane. */
  totalMatches?: number
  /** Filters the search ran — echoed as lastFilters so follow-ups refine. */
  appliedFilters?: SearchFilters
  actions?: ChatAction[]
  /** Supplier confirm payload — renders [Yes][Skip] on the bubble.
   * `resolved` marks that the supplier already acted so the buttons hide. */
  confirm?: ChatConfirm
  confirmResolved?: 'done' | 'skipped'
  ts: number
}

/** One conversation. `messages` is the full transcript; `hydrated` marks
 * whether a server-listed session has had its messages loaded yet. */
interface Session {
  id: string
  title: string
  messages: ChatMessage[]
  updatedAt: number
  hydrated: boolean
}

/**
 * /chat — the conversational way around the platform.
 *
 * Chat is not a single ephemeral thread: it's a multi-session surface
 * (like the mobile app's recent-chats drawer). A sessions sidebar lists
 * every past conversation; "New chat" starts a fresh one; picking a past
 * session reloads its transcript. Signed-in users persist through the
 * chat-history API (Firestore, cross-device); anonymous users keep a
 * localStorage-only mirror scoped to `anon`.
 *
 * Above the composer, role-aware quick-action chips turn chat into
 * navigation — buyers jump to orders/shortlist, suppliers to stock /
 * mark-unavailable / update-price, admins to merchants / ledger. Assistant
 * `actions` pills returned by the API navigate to real routes.
 *
 * Pre-loads a starter prompt from ?q= so the Landing hero composer can
 * hand off with the user's typed query already pending.
 */

// Storage is scoped by uid (or 'anon') so signing in/out swaps histories
// cleanly and one user never sees another's threads on a shared device.
const STORAGE_PREFIX = 'aska:chat:v2:'
const MAX_STORED_SESSIONS = 20

function scopeStorageKey(scope: string): string {
  return `${STORAGE_PREFIX}${scope}`
}

interface StoredScope {
  sessions: Session[]
  activeId: string | null
}

/**
 * An "empty" session — locally created, never got a message or a title. These
 * are what pile up when "New chat" (or opening the Ask Gemma page) mints a fresh
 * session that's then abandoned. We reuse/prune them so the Recent list doesn't
 * fill with blank "New chat" rows. `hydrated === false` = a server session whose
 * messages just haven't loaded yet — NOT empty, so it's excluded from the check.
 */
function isEmptySession(s: Session): boolean {
  return s.hydrated !== false && s.messages.length === 0 && !(s.title && s.title.trim())
}

function loadScope(scope: string): StoredScope {
  if (typeof window === 'undefined') return { sessions: [], activeId: null }
  try {
    const raw = window.localStorage.getItem(scopeStorageKey(scope))
    if (!raw) return { sessions: [], activeId: null }
    const parsed = JSON.parse(raw) as StoredScope
    const sessions = (parsed.sessions || []).map((s) => ({
      ...s,
      // Drop any messages still flagged thinking — they were mid-flight
      // when the tab closed and never got their real text.
      messages: (s.messages || []).filter((m) => !m.thinking),
      hydrated: true,
    }))
      // Prune abandoned empty sessions accumulated by prior visits.
      .filter((s) => !isEmptySession(s))
    return { sessions, activeId: parsed.activeId ?? sessions[0]?.id ?? null }
  } catch {
    return { sessions: [], activeId: null }
  }
}

function saveScope(scope: string, data: StoredScope): void {
  if (typeof window === 'undefined') return
  try {
    const sessions = data.sessions
      .slice(0, MAX_STORED_SESSIONS)
      .map((s) => ({ ...s, messages: s.messages.filter((m) => !m.thinking) }))
    window.localStorage.setItem(
      scopeStorageKey(scope),
      JSON.stringify({ sessions, activeId: data.activeId }),
    )
  } catch { /* quota — best effort */ }
}

/** Firestore serverTimestamp serializes as {_seconds,_nanoseconds} over
 * JSON; also tolerate ISO strings and raw millis. */
function tsToMillis(v: unknown): number | null {
  if (typeof v === 'number') return v
  if (typeof v === 'string') { const t = Date.parse(v); return Number.isNaN(t) ? null : t }
  if (v && typeof v === 'object') {
    const o = v as Record<string, number>
    const s = o._seconds ?? o.seconds
    if (typeof s === 'number') return s * 1000
  }
  return null
}

/** Order-intent detector — mirrors the backend heuristicParse support/order
 * split so "problem with my order" (support) doesn't pull order status. */
// Buyer order status is a server-side TOOL now (get_my_orders) — the agent
// fetches it itself; no client pre-fetch needed.

/** Admin metric-intent detector — "how many …", stats, counts, KPIs. */
const ADMIN_METRIC_RE = /\b(how many|how much|count|number of|stats|statistics|metrics|kpi|snapshot|overview|total|volume|dashboard)\b/i

/**
 * Fetch live platform KPIs (GET /admin/summary) and format them for the admin
 * AI's systemContext, so "how many stones/users/enquiries" answers with real
 * numbers (admin-persona quotes provided figures verbatim). Only runs for
 * admins asking a metric question; failures fall back to generic guidance.
 */
async function buildAdminContext(userText: string): Promise<string | undefined> {
  if (!ADMIN_METRIC_RE.test(userText)) return undefined
  try {
    const s = await adminApi.summary()
    return `=== LIVE PLATFORM METRICS (quote these figures; do not invent others) ===\n`
      + `- Stones (total catalog): ${s.stones.toLocaleString()}\n`
      + `- Users (all roles): ${s.users.toLocaleString()}\n`
      + `- Enquiries (buyer→supplier, total): ${s.enquiries.toLocaleString()}\n`
      + `=== END ===`
  } catch {
    return undefined
  }
}

function deriveTitle(text: string): string {
  const t = text.trim().replace(/\s+/g, ' ')
  return t.length > 42 ? `${t.slice(0, 42)}…` : t
}

/** Compact mono timestamp for the sessions rail — clock for today, else a
 * short date. Sits in IBM Plex Mono (the "technical" metadata layer). */
function formatClock(ms: number): string {
  const d = new Date(ms)
  const now = new Date()
  const sameDay = d.toDateString() === now.toDateString()
  if (sameDay) return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false })
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

type SessionGroup = 'Today' | 'Yesterday' | 'Earlier'

/** Buckets a timestamp into Today / Yesterday / Earlier (calendar-day, not
 * rolling 24h) so the rail groups the way a person thinks about them. */
function sessionGroupOf(ms: number): SessionGroup {
  const d = new Date(ms)
  const now = new Date()
  if (d.toDateString() === now.toDateString()) return 'Today'
  const yest = new Date(now)
  yest.setDate(now.getDate() - 1)
  if (d.toDateString() === yest.toDateString()) return 'Yesterday'
  return 'Earlier'
}

/** Splits sessions (already sorted newest-first) into ordered groups,
 * dropping empty buckets so headings only show when they have rows. */
function groupSessions(sessions: Session[]): { label: SessionGroup; rows: Session[] }[] {
  const order: SessionGroup[] = ['Today', 'Yesterday', 'Earlier']
  const buckets = new Map<SessionGroup, Session[]>()
  for (const s of sessions) {
    const g = sessionGroupOf(s.updatedAt)
    const arr = buckets.get(g) ?? []
    arr.push(s)
    buckets.set(g, arr)
  }
  return order
    .map((label) => ({ label, rows: buckets.get(label) ?? [] }))
    .filter((g) => g.rows.length > 0)
}

function mapStored(sm: StoredChatMessage): ChatMessage {
  let results: Diamond[] | undefined
  if (sm.resultSetJson) {
    try {
      const parsed = JSON.parse(sm.resultSetJson)
      if (Array.isArray(parsed)) results = parsed as Diamond[]
    } catch { /* ignore malformed cache */ }
  }
  return {
    id: sm.messageId,
    role: sm.role,
    text: sm.text,
    ts: tsToMillis(sm.ts) ?? Date.now(),
    results,
  }
}

// ── Role-aware quick actions — chat as the way around the platform ──
interface QuickAction { label: string; prompt?: string; route?: string }

function quickActionsFor(role: Role): QuickAction[] {
  if (role === 'supplier') {
    return [
      { label: 'My stock', route: '/seller/mystock' },
      { label: 'Mark unavailable', prompt: 'I need to mark one of my stones as unavailable' },
      { label: 'Update price', prompt: 'I want to update the price on one of my stones' },
      { label: 'My inbox', route: '/seller-inbox' },
    ]
  }
  if (role === 'admin') {
    // Platform-ops copilot: metrics, users/KYC, orders, marketing, merchants, ledger.
    return [
      { label: 'Platform stats', prompt: 'Give me a snapshot of the platform — how many stones, users, and open enquiries do we have?' },
      { label: 'KYC queue', route: '/admin?tab=users' },
      { label: 'Orders', route: '/admin?tab=orders' },
      { label: 'Marketing', route: '/admin?tab=marketing' },
      { label: 'Merchants', route: '/admin?tab=merchants' },
      { label: 'Fees ledger', route: '/admin?tab=ledger' },
      { label: 'Enquiries', route: '/admin?tab=enquiries' },
    ]
  }
  // buyer + anonymous — v0 directory: no orders / cart (both routes retired).
  return [
    { label: 'Find a diamond', prompt: "I'm looking for a diamond — help me find one" },
    { label: 'Favorite suppliers', route: '/favorites' },
    { label: 'My shares', route: '/shares' },
  ]
}

const SUGGESTION_CHIPS = [
  'Round 1-2ct under $5k',
  'Oval D color VS+',
  'What is the biggest stone you have',
  'Compare AJ001 and AJ005',
  'How does shipping work',
]

export function Chat() {
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const { signedIn: storeSignedIn } = useApp()

  // Auth-derived scope + role. `scope` keys storage; role drives the
  // quick-action chips. Both resolve async on first auth tick.
  const [scope, setScope] = useState('anon')
  const [role, setRole] = useState<Role>(null)
  const signedIn = scope !== 'anon'

  const [sessions, setSessions] = useState<Session[]>([])
  const [activeId, setActiveId] = useState<string | null>(null)
  const [input, setInput] = useState('')
  const [sending, setSending] = useState(false)
  const [openStone, setOpenStone] = useState<Diamond | null>(null)
  const [dictation, setDictation] = useState<SttSession | null>(null)
  const [mobileSidebar, setMobileSidebar] = useState(false)

  const inputRef = useRef<HTMLTextAreaElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const initialQuery = params.get('q')
  const queryHandled = useRef(false)

  // Refs mirror state so async callbacks read fresh values without
  // re-subscribing.
  const sessionsRef = useRef<Session[]>(sessions)
  sessionsRef.current = sessions
  const activeIdRef = useRef<string | null>(activeId)
  activeIdRef.current = activeId
  const scopeRef = useRef(scope)
  scopeRef.current = scope

  const activeSession = useMemo(
    () => sessions.find((s) => s.id === activeId) ?? null,
    [sessions, activeId],
  )
  const messages = activeSession?.messages ?? []
  const hasMessages = messages.length > 0

  // The CURRENT results = the LATEST assistant turn that actually returned
  // stones. Powers the wide-desktop right pane; when it's empty the pane is
  // hidden entirely (the hero + composer then center cleanly). A thinking
  // placeholder carries no results, so this naturally holds the previous set
  // steady until the next turn resolves. Below lg the pane never mounts and
  // results stay inline in the conversation as before.
  const { latestResults, latestResultsMsgId, latestTotalMatches, latestBrowseAll } = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i]
      if (m.role === 'assistant' && m.results && m.results.length > 0) {
        // The "Browse all N matches" pill the server already attaches carries
        // the conversation's filters as /search?…; reuse THAT so the pane's
        // browse-all button and the bubble's pill go to the identical place.
        const browseAll = m.actions?.find((a) => a.route.startsWith('/search')) ?? null
        return {
          latestResults: m.results,
          latestResultsMsgId: m.id,
          latestTotalMatches: m.totalMatches,
          latestBrowseAll: browseAll,
        }
      }
    }
    return {
      latestResults: [] as Diamond[],
      latestResultsMsgId: null as string | null,
      latestTotalMatches: undefined as number | undefined,
      latestBrowseAll: null as ChatAction | null,
    }
  }, [messages])
  const hasResults = latestResults.length > 0

  // The Recent list hides empty sessions (except the active one you're typing
  // into) so abandoned "New chat" rows never accumulate.
  const visibleSessions = useMemo(
    () => sessions.filter((s) => s.id === activeId || !isEmptySession(s)),
    [sessions, activeId],
  )

  // ── Auth: resolve scope + role ──────────────────────────────────
  useEffect(() => {
    let unsub: (() => void) | undefined
    try {
      unsub = watchAuthState(async (user) => {
        if (!user) { setScope('anon'); setRole(null); return }
        setScope(user.uid)
        try {
          const tok = await user.getIdTokenResult()
          const r = (tok.claims as { role?: string }).role
          setRole(r === 'admin' || r === 'supplier' ? r : 'buyer')
        } catch { setRole('buyer') }
      })
    } catch { setScope('anon') }
    return () => unsub?.()
  }, [])

  // ── Load a scope's sessions when it resolves, then merge server ──
  useEffect(() => {
    const local = loadScope(scope)
    setSessions(local.sessions)
    setActiveId(local.activeId ?? local.sessions[0]?.id ?? null)

    if (scope === 'anon') return
    let cancelled = false
    chatHistoryApi.sessions()
      .then((r) => { if (!cancelled) setSessions((prev) => mergeServerSessions(prev, r.sessions)) })
      .catch(() => { /* backend not live / offline — localStorage stands in */ })
    return () => { cancelled = true }
  }, [scope])

  // ── Persist the active scope on every change ────────────────────
  useEffect(() => {
    saveScope(scopeRef.current, { sessions, activeId })
  }, [sessions, activeId])

  // Keep the transcript scrolled to the newest message.
  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight
  }, [messages.length, activeId])

  // ── ⌘N / Ctrl+N → new chat (keyboard-first). Ignored while a modifier
  //    combo the browser owns is in play; preventDefault stops the browser's
  //    own "new window" so the shortcut is ours on this surface. ──────────
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && !e.shiftKey && !e.altKey && (e.key === 'n' || e.key === 'N')) {
        e.preventDefault()
        onNewChat()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
    // onNewChat is a stable closure over refs/setters — safe to bind once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ── One-time ?q= hand-off from the Landing hero ─────────────────
  useEffect(() => {
    if (!initialQuery || queryHandled.current) return
    queryHandled.current = true
    // Start the hand-off in a fresh session so it never appends onto an
    // old transcript the user was mid-way through.
    const id = createSession()
    void sendMessage(initialQuery, id)
    setParams({}, { replace: true })
  }, [initialQuery])

  function mergeServerSessions(local: Session[], metas: ChatSessionMeta[]): Session[] {
    const byId = new Map(local.map((s) => [s.id, s]))
    for (const m of metas) {
      const updatedAt = tsToMillis(m.updatedAt) ?? Date.now()
      const existing = byId.get(m.sessionId)
      if (existing) {
        byId.set(m.sessionId, {
          ...existing,
          title: existing.title || (m.title ?? ''),
          updatedAt: Math.max(existing.updatedAt, updatedAt),
        })
      } else {
        byId.set(m.sessionId, {
          id: m.sessionId,
          title: m.title ?? '',
          messages: [],
          updatedAt,
          hydrated: false, // messages loaded lazily when opened
        })
      }
    }
    return [...byId.values()].sort((a, b) => b.updatedAt - a.updatedAt).slice(0, 30)
  }

  function patchSession(id: string, fn: (s: Session) => Session) {
    setSessions((prev) => prev.map((s) => (s.id === id ? fn(s) : s)))
  }

  function createSession(): string {
    // Reuse an existing empty session instead of minting another — this is what
    // stops the Recent list from filling with blank "New chat" rows when the
    // page is opened repeatedly or "New chat" is tapped without sending.
    const existingEmpty = sessionsRef.current.find(isEmptySession)
    if (existingEmpty) {
      setActiveId(existingEmpty.id)
      return existingEmpty.id
    }
    const id = crypto.randomUUID()
    setSessions((prev) => [
      { id, title: '', messages: [], updatedAt: Date.now(), hydrated: true },
      ...prev,
    ])
    setActiveId(id)
    return id
  }

  function onNewChat() {
    setInput('')
    setMobileSidebar(false)
    createSession()
    inputRef.current?.focus()
  }

  function selectSession(id: string) {
    setActiveId(id)
    setMobileSidebar(false)
    const s = sessionsRef.current.find((x) => x.id === id)
    if (s && !s.hydrated && signedIn) {
      chatHistoryApi.session(id)
        .then((r) => patchSession(id, (ss) => ({ ...ss, messages: r.messages.map(mapStored), hydrated: true })))
        .catch(() => patchSession(id, (ss) => ({ ...ss, hydrated: true })))
    }
  }

  function deleteSession(id: string) {
    if (signedIn) chatHistoryApi.archive(id).catch(() => {})
    const next = sessionsRef.current.filter((s) => s.id !== id)
    setSessions(next)
    if (activeIdRef.current === id) setActiveId(next[0]?.id ?? null)
  }

  async function sendMessage(text: string, forceSessionId?: string) {
    const trimmed = text.trim()
    if (!trimmed || sending) return
    setInput('')

    // Ensure a session to write into.
    let id = forceSessionId ?? activeIdRef.current
    if (!id || !sessionsRef.current.find((s) => s.id === id)) id = createSession()

    const prev = sessionsRef.current.find((s) => s.id === id)
    const isFirstUserMsg = !prev || prev.messages.filter((m) => m.role === 'user').length === 0
    const title = isFirstUserMsg ? deriveTitle(trimmed) : (prev?.title || '')

    const userMsg: ChatMessage = { id: crypto.randomUUID(), role: 'user', text: trimmed, ts: Date.now() }
    const placeholderId = crypto.randomUUID()
    const placeholder: ChatMessage = {
      id: placeholderId, role: 'assistant', text: pickPlaceholder(trimmed), thinking: true, ts: Date.now() + 1,
    }

    // History = the turns BEFORE this one (prev still holds the old state).
    const history: ChatMessageHistory[] = (prev?.messages ?? [])
      .slice(-6)
      .filter((m) => m.role !== 'system' && !m.thinking)
      .map((m) => ({ role: m.role as 'user' | 'assistant', text: m.text }))

    patchSession(id, (s) => ({
      ...s,
      title: s.title || title,
      updatedAt: Date.now(),
      messages: [...s.messages, userMsg, placeholder],
    }))
    setSending(true)

    // Persist the user turn (best-effort — localStorage already has it).
    if (signedIn) {
      chatHistoryApi.append({
        sessionId: id, role: 'user', text: trimmed,
        title: isFirstUserMsg ? title : undefined,
      }).catch(() => {})
    }

    // Admin-only live context (platform KPIs). Buyer order status + supplier
    // feed checks are server-side TOOLS now — the agent fetches them itself.
    const roleContext = signedIn && role === 'admin' ? await buildAdminContext(trimmed) : undefined

    // The most recent search's filters — lets "only VS1" refine server-side.
    const lastFilters = [...(prev?.messages ?? [])].reverse()
      .find((m) => m.role === 'assistant' && m.appliedFilters)?.appliedFilters

    const sessionId = id
    try {
      const r = await chatApi.send({
        userText: trimmed,
        conversationHistory: history,
        lastFilters,
        isAuthenticated: signedIn,
        role: role ?? undefined,
        systemContext: roleContext,
      })
      patchSession(sessionId, (s) => ({
        ...s,
        updatedAt: Date.now(),
        messages: s.messages.map((m) =>
          m.id === placeholderId
            ? { ...m, text: r.response, thinking: false, steps: r.steps, results: r.results,
                totalMatches: r.totalMatches,
                appliedFilters: r.appliedFilters, actions: r.actions, confirm: r.confirm }
            : m,
        ),
      }))
      if (signedIn) {
        chatHistoryApi.append({
          sessionId, role: 'assistant', text: r.response,
          resultSetJson: r.results && r.results.length ? JSON.stringify(r.results.slice(0, 12)) : undefined,
        }).catch(() => {})
      }
    } catch (err) {
      const text =
        err instanceof ApiError
          ? `Couldn't reach the assistant (${err.status}). Try again in a moment.`
          : "Couldn't reach the assistant. Try again in a moment."
      patchSession(sessionId, (s) => ({
        ...s,
        messages: s.messages.map((m) => (m.id === placeholderId ? { ...m, text, thinking: false } : m)),
      }))
    } finally {
      setSending(false)
      inputRef.current?.focus()
    }
  }

  function pickPlaceholder(userText: string): string {
    const t = userText.toLowerCase()
    if (/order|invoice|payment/.test(t)) return 'Looking at your orders…'
    if (/cart|shortlist/.test(t)) return 'Pulling up your cart…'
    if (/unavailable|out of stock|sold/.test(t)) return 'Checking your stock…'
    if (/price|repric/.test(t)) return 'Looking up that stone…'
    if (/round|oval|pear|emerald|cushion|princess|marquise|heart|asscher|radiant/.test(t)) {
      return 'Searching the inventory…'
    }
    if (/biggest|largest|cheapest|most expensive/.test(t)) return 'Scanning current stock…'
    return 'Thinking…'
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      void sendMessage(input)
    }
  }

  function onQuickAction(a: QuickAction) {
    if (a.route) { navigate(a.route); return }
    if (a.prompt) void sendMessage(a.prompt)
  }

  function toggleVoice() {
    if (dictation) { dictation.stop(); setDictation(null); return }
    if (!isSttAvailable()) {
      const id = activeIdRef.current ?? createSession()
      patchSession(id, (s) => ({
        ...s,
        messages: [...s.messages, {
          id: crypto.randomUUID(), role: 'system',
          text: "Voice isn't supported in this browser yet — try Chrome or Safari, or just type.",
          ts: Date.now(),
        }],
      }))
      return
    }
    const session = startStt({
      onText: (full: string) => setInput(full),
      onError: () => setDictation(null),
      onEnd: () => setDictation(null),
    })
    if (session) setDictation(session)
  }

  /**
   * Supplier/admin acts on an inventory-update confirm card. On Yes we call
   * the matching /seller endpoint (server re-checks the token role + stone
   * ownership, so a buyer can never write here even if a confirm card leaked
   * to them). We append a system line reporting the outcome and mark the
   * confirm resolved so the buttons disappear. update_stock maps price→rate;
   * the backend only applies rate/cut today, so color/clarity are reported as
   * unsupported rather than silently dropped.
   */
  async function onConfirmSupplierAction(msgId: string, confirm: ChatConfirm, choice: 'yes' | 'skip') {
    const sid = activeIdRef.current
    if (choice === 'skip') {
      patchSession(sid!, (s) => ({
        ...s,
        messages: s.messages.map((m) => (m.id === msgId ? { ...m, confirmResolved: 'skipped' } : m)),
      }))
      return
    }
    let outcome = ''
    try {
      if (confirm.payload.kind === 'mark_unavailable') {
        const r = await sellerApi.markUnavailable(confirm.payload.stockIds)
        outcome = r.flagged.length
          ? `Marked ${r.flagged.join(', ')} as unavailable.`
          : `Couldn't mark those${r.skipped[0] ? ` (${r.skipped[0].reason})` : ''}.`
      } else if (confirm.payload.kind === 'update_contact') {
        await sellerApi.updateContact(confirm.payload.updates)
        outcome = 'Contact updated — verified buyers now see the new details on your stones.'
      } else {
        const u = confirm.payload.updates
        const stone: { stockId: string; rate?: number; cut?: string } = { stockId: confirm.payload.stockId }
        const unsupported: string[] = []
        if (typeof u.price === 'number') stone.rate = u.price
        if (u.cut) stone.cut = u.cut
        if (u.color) unsupported.push('color')
        if (u.clarity) unsupported.push('clarity')
        if (u.carat) unsupported.push('carat')
        if (stone.rate == null && stone.cut == null) {
          outcome = unsupported.length
            ? `${unsupported.join('/')} updates aren't supported from chat yet — edit that stone in My stock.`
            : "Nothing to update."
        } else {
          const r = await sellerApi.updateStock([stone])
          outcome = r.updated.length
            ? `Updated ${r.updated.join(', ')}.${unsupported.length ? ` (${unsupported.join('/')} not supported here — use My stock.)` : ''}`
            : `Couldn't update${r.skipped[0] ? ` (${r.skipped[0].reason})` : ''}.`
        }
      }
    } catch {
      outcome = "That update didn't go through — try again from My stock."
    }
    patchSession(sid!, (s) => ({
      ...s,
      messages: [
        ...s.messages.map((m) => (m.id === msgId ? { ...m, confirmResolved: 'done' as const } : m)),
        { id: crypto.randomUUID(), role: 'system' as const, text: outcome, ts: Date.now() },
      ],
    }))
  }

  const quickActions = quickActionsFor(role)

  return (
    <AppLayout noFooter>
      <div className="relative mx-auto max-w-[1600px] w-full px-2 sm:px-4 md:px-6 lg:px-8 py-4 md:py-6 flex justify-center gap-4 md:gap-5 lg:gap-6 h-[calc(100dvh-4.25rem)]">
        {/* Ambient wash — tinted, off-brand-neutral blobs so the frosted pane
            has tonal variation BEHIND it to bend/saturate (frost only reads as
            glass over texture). Warm gold + a cool navy whisper for depth. */}
        <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden" aria-hidden>
          <div className="absolute left-[42%] top-[22%] h-[460px] w-[640px] -translate-x-1/2 rounded-full bg-gold/[0.10] blur-3xl" />
          <div className="absolute right-[6%] bottom-[8%] h-[380px] w-[440px] rounded-full bg-navy/[0.07] blur-3xl" />
          <div className="absolute left-[4%] bottom-[18%] h-[300px] w-[360px] rounded-full bg-gold/[0.06] blur-3xl" />
        </div>
        {/* Sessions sidebar — desktop */}
        <Sidebar
          sessions={visibleSessions}
          activeId={activeId}
          onSelect={selectSession}
          onNew={onNewChat}
          onDelete={deleteSession}
          className="hidden md:flex"
        />

        {/* Mobile sessions drawer */}
        <AnimatePresence>
          {mobileSidebar && (
            <>
              <motion.div
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="md:hidden fixed inset-0 z-[60] bg-navy/40 backdrop-blur-sm"
                onClick={() => setMobileSidebar(false)}
                aria-hidden
              />
              <motion.div
                initial={{ x: '-100%' }} animate={{ x: 0 }} exit={{ x: '-100%' }}
                transition={{ type: 'spring', stiffness: 320, damping: 32 }}
                className="md:hidden fixed left-0 top-0 bottom-0 z-[70] w-[80%] max-w-[300px] p-2.5"
              >
                <Sidebar
                  sessions={visibleSessions}
                  activeId={activeId}
                  onSelect={selectSession}
                  onNew={onNewChat}
                  onDelete={deleteSession}
                  className="flex w-full h-full !rounded-2xl"
                />
              </motion.div>
            </>
          )}
        </AnimatePresence>

        {/* Chat column — a FLOATING frosted-glass pane lifting off the page.
            It fills its flex track (no narrow max-w-3xl island — that double-
            centered against the mx-auto container and left a big dead left
            margin). When there are NO results (hero / fresh chat) it stays a
            centered readable panel so the hero + composer sit cleanly; once
            results arrive on wide desktop the right ResultsPane fills the
            former right gap. */}
        <div className={`lqd-pane flex-1 min-w-0 flex flex-col w-full rounded-[1.25rem] px-3 sm:px-4 md:px-6 pt-3 md:pt-4 pb-3 md:pb-4 overflow-hidden ${hasResults ? 'lg:max-w-[52rem]' : 'max-w-3xl mx-auto'}`}>
          {/* Faint dot-grid so the frosted glass has STRUCTURE to bend — very
              low opacity, radially masked toward the top, both themes. */}
          <div className="lqd-dotgrid pointer-events-none absolute inset-0 -z-[1]" aria-hidden />
          {/* Mobile: open sessions + new chat */}
          <div className="md:hidden flex items-center justify-between mb-2">
            <button
              type="button"
              onClick={() => setMobileSidebar(true)}
              className="lqd-actionpill inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[13px] font-medium"
              data-testid="chat-sessions-open"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" />
              </svg>
              Chats
            </button>
            <button
              type="button"
              onClick={onNewChat}
              className="lqd-newchat px-3 py-1.5 text-[13px] font-medium"
              data-testid="chat-new-mobile"
            >
              <span className="lqd-newchat__plus" aria-hidden>
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
                </svg>
              </span>
              New chat
            </button>
          </div>

          {!hasMessages ? (
            <Hero role={role} onSuggestion={(s) => void sendMessage(s)} />
          ) : (
            <div ref={listRef} className="flex-1 overflow-y-auto pb-4 space-y-5">
              <AnimatePresence initial={false}>
                {messages.map((m) => (
                  <Bubble
                    key={m.id}
                    msg={m}
                    blurPrice={!storeSignedIn}
                    onOpenStone={setOpenStone}
                    onAction={(a) => navigate(a.route)}
                    onConfirm={onConfirmSupplierAction}
                    // This turn's results are mirrored in the wide-desktop right
                    // pane, so hide the inline rows at lg+ (they stay inline
                    // below lg where the pane never mounts). Older turns keep
                    // their inline results everywhere.
                    hideInlineResults={m.id === latestResultsMsgId}
                  />
                ))}
              </AnimatePresence>
            </div>
          )}

          {/* Role-aware quick actions — the way around the platform */}
          <div className="flex items-center gap-1.5 overflow-x-auto pb-1 -mx-1 px-1" data-testid="chat-quickactions">
            {quickActions.map((a) => (
              <button
                key={a.label}
                type="button"
                onClick={() => onQuickAction(a)}
                className="lqd-actionpill shrink-0 inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-[12.5px] font-medium"
                data-testid="chat-quickaction"
              >
                {a.route && (
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" className="text-soft">
                    <path d="M7 17 17 7" /><path d="M7 7h10v10" />
                  </svg>
                )}
                {a.label}
              </button>
            ))}
          </div>

          <Composer
            input={input}
            onChange={setInput}
            onSend={() => void sendMessage(input)}
            onKeyDown={onKeyDown}
            sending={sending}
            dictating={!!dictation}
            onToggleVoice={toggleVoice}
            inputRef={inputRef}
          />
        </div>

        {/* RIGHT — wide-desktop results pane. Once the latest Gemma turn has
            stones it mounts here (subtle enter) and fills the former dead right
            gap; the center pane widens to meet it. With NO results a spacer the
            width of the rail keeps the chat column centered in the viewport
            rather than pushed right. Below lg neither shows and results stay
            inline in the conversation. */}
        <AnimatePresence mode="wait">
          {hasResults ? (
            <ResultsPane
              key="results"
              results={latestResults}
              totalMatches={latestTotalMatches}
              blurPrice={!storeSignedIn}
              onOpenStone={setOpenStone}
              onBrowseAll={latestBrowseAll ? () => navigate(latestBrowseAll.route) : undefined}
            />
          ) : (
            <div key="spacer" className="hidden lg:block w-64 shrink-0" aria-hidden />
          )}
        </AnimatePresence>
      </div>

      <MediaModal
        open={!!openStone}
        onClose={() => setOpenStone(null)}
        stone={openStone}
        blurPrice={!storeSignedIn}
      />
    </AppLayout>
  )
}

// ────────────────────────────────────────────────────────────────

function Sidebar({
  sessions, activeId, onSelect, onNew, onDelete, className = '',
}: {
  sessions: Session[]
  activeId: string | null
  onSelect: (id: string) => void
  onNew: () => void
  onDelete: (id: string) => void
  className?: string
}) {
  const [query, setQuery] = useState('')

  // Filter by title (case-insensitive); a session with no title yet still
  // matches an empty query. Grouping runs on the filtered set.
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return sessions
    return sessions.filter((s) => (s.title || 'new chat').toLowerCase().includes(q))
  }, [sessions, query])
  const groups = useMemo(() => groupSessions(filtered), [filtered])

  return (
    <aside
      className={`lqd-rail w-64 shrink-0 flex-col rounded-[1.25rem] p-3 ${className}`}
      data-testid="chat-sessions"
    >
      {/* New chat — refined technical button: hairline + whisper fill, a +
          glyph in a hairline square, and a muted-mono ⌘N shortcut badge. */}
      <button
        type="button"
        onClick={onNew}
        className="lqd-newchat mb-2.5 w-full px-2.5 py-2 text-[13px] font-medium"
        data-testid="chat-new"
      >
        <span className="lqd-newchat__plus" aria-hidden>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
          </svg>
        </span>
        New chat
        <span className="lqd-kbd" aria-hidden>⌘N</span>
      </button>

      {/* Search chats — frosted hairline input with a mono placeholder feel. */}
      <label className="lqd-railsearch mb-2.5 flex items-center gap-1.5 px-2.5 py-1.5">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 text-soft" aria-hidden>
          <circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search chats"
          className="lqd-mono flex-1 min-w-0 bg-transparent text-[12px] text-ink placeholder:text-soft focus:outline-none"
          data-testid="chat-search"
        />
      </label>

      <div className="flex-1 overflow-y-auto pr-0.5">
        {sessions.length === 0 ? (
          <p className="px-2 py-3 text-[12.5px] text-soft">No conversations yet. Start one below.</p>
        ) : groups.length === 0 ? (
          <p className="lqd-mono px-2 py-3 text-[11px] text-soft">No matches for “{query.trim()}”.</p>
        ) : (
          groups.map((g) => (
            <div key={g.label} className="mb-2">
              <p className="lqd-railgroup px-2 pb-1 pt-0.5">{g.label}</p>
              <div className="space-y-0.5">
                {g.rows.map((s) => (
                  <div
                    key={s.id}
                    className={`lqd-railrow2 group flex items-center ${
                      s.id === activeId ? 'lqd-railrow2--active' : ''
                    }`}
                    data-testid="chat-session"
                    data-active={s.id === activeId ? 'true' : 'false'}
                  >
                    <button
                      type="button"
                      onClick={() => onSelect(s.id)}
                      className="flex-1 min-w-0 flex items-center gap-2 text-left px-2.5 py-2"
                    >
                      <span className="flex-1 min-w-0">
                        <span className="block truncate text-[13px] font-medium text-ink">
                          {s.title || 'New chat'}
                        </span>
                      </span>
                      <span className="lqd-mono shrink-0 text-[10px] text-soft tabular-nums">
                        {formatClock(s.updatedAt)}
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => onDelete(s.id)}
                      aria-label="Delete chat"
                      className="shrink-0 mr-1 inline-flex h-7 w-7 items-center justify-center rounded-md text-soft opacity-0 group-hover:opacity-100 focus-visible:opacity-100 hover:text-danger hover:bg-paper transition-all"
                      data-testid="chat-session-delete"
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </aside>
  )
}

function Hero({ role, onSuggestion }: { role: Role; onSuggestion: (s: string) => void }) {
  const heading =
    role === 'supplier' ? 'How can I help with your inventory?'
      : role === 'admin' ? 'What would you like to manage?'
        : 'What diamond can I find for you today?'
  const sub =
    role === 'supplier' ? 'Ask about your stock, mark stones unavailable, or update a price — just describe it.'
      : role === 'admin' ? 'Ask about merchants, the fees ledger, KYC, or jump to any admin surface.'
        : "Describe what you're looking for. Shape, size, color, budget — I'll find matches in our inventory."
  return (
    <div className="flex-1 flex flex-col items-center justify-center text-center">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="mb-8"
      >
        {/* Deliberate facet mark — two thin rotating conic rings (the "facet
            ring") around a solid navy gem disc. Restrained; the spin freezes
            under reduced-motion (handled in .lqd-facetring CSS). */}
        <div className="relative h-24 w-24 mx-auto">
          <div className="lqd-facetring" aria-hidden />
          <div className="absolute inset-[10px] rounded-full bg-gradient-to-br from-[#12315b] to-[#0a1c34] shadow-card flex items-center justify-center ring-1 ring-gold/25">
            <svg width="38" height="38" viewBox="0 0 24 24" fill="none" stroke="#E7C583" strokeWidth="1.4" strokeLinejoin="round" strokeLinecap="round">
              <polygon points="12 2 19 9 12 22 5 9" fill="rgba(231,197,131,0.20)" />
              <line x1="5" y1="9" x2="19" y2="9" />
              <line x1="9" y1="9" x2="12" y2="22" />
              <line x1="15" y1="9" x2="12" y2="22" />
            </svg>
          </div>
        </div>
      </motion.div>
      <motion.h1
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15, duration: 0.45 }}
        className="text-3xl md:text-4xl font-extrabold tracking-tight text-ink"
      >
        {heading}
      </motion.h1>
      {/* Technical byline — mono, muted, wide-tracked. The tell. */}
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.24 }}
        className="lqd-mono mt-3 text-[10.5px] uppercase tracking-[0.22em] text-soft"
      >
        DiaEx AI · beta
      </motion.p>
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="mt-3 text-sm text-soft max-w-md"
      >
        {sub}
      </motion.p>

      {(role === null || role === 'buyer') && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45 }}
          className="mt-10 flex flex-wrap items-center justify-center gap-2"
        >
          {SUGGESTION_CHIPS.map((chip) => (
            <button
              key={chip}
              type="button"
              onClick={() => onSuggestion(chip)}
              className="lqd-techchip inline-flex items-center rounded-lg px-3.5 py-1.5 text-[12.5px] font-medium tracking-tight"
            >
              {chip}
            </button>
          ))}
        </motion.div>
      )}
    </div>
  )
}

function Bubble({
  msg, blurPrice, onOpenStone, onAction, onConfirm, hideInlineResults = false,
}: {
  msg: ChatMessage
  blurPrice: boolean
  onOpenStone: (s: Diamond) => void
  onAction: (a: ChatAction) => void
  onConfirm: (msgId: string, confirm: ChatConfirm, choice: 'yes' | 'skip') => void
  /** When true, the inline result rows collapse at lg+ because this turn's
   *  stones are shown in the wide-desktop right ResultsPane instead. Below lg
   *  the pane never mounts, so the rows stay inline. */
  hideInlineResults?: boolean
}) {
  if (msg.role === 'system') {
    return <p className="text-center text-[12px] text-soft italic">{msg.text}</p>
  }
  if (msg.role === 'user') {
    return (
      <motion.div
        layout
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex justify-end"
        data-testid="msg-user"
      >
        <p className="lqd-user max-w-[85%] rounded-2xl rounded-br-md px-4 py-2.5 text-[15px] text-white">
          {msg.text}
        </p>
      </motion.div>
    )
  }
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex flex-col gap-3"
      data-testid="msg-assistant"
    >
      {/* Light agentic trail — what Gemma actually did, with counts.
          Mono metadata layer (the "technical" tell). */}
      {!msg.thinking && msg.steps && msg.steps.length > 0 && (
        <div className="flex flex-col gap-0.5" data-testid="msg-steps">
          {msg.steps.map((s, i) => (
            <p key={i} className="lqd-step flex items-center gap-1.5 leading-snug">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 text-gold">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              <span className="truncate">{s.label}{s.detail ? <span className="opacity-70"> — {s.detail}</span> : null}</span>
            </p>
          ))}
        </div>
      )}
      {/* Thinking state: shimmer text + a blinking caret (the only "live"
          state — text is replaced wholesale when the turn resolves), plus a
          thin sweeping thinkbar underneath. */}
      <p className={
        'max-w-[92%] text-[15px] leading-[1.6] whitespace-pre-wrap ' +
        (msg.thinking ? 'thinking-shimmer lqd-caret' : 'text-ink')
      }>
        {/* The backend appends " [Yes] [Skip]" to confirm prompts as a text
            fallback; we render real buttons below, so strip the literal. */}
        {msg.confirm ? msg.text.replace(/\s*\[Yes\]\s*\[Skip\]\s*$/i, '') : msg.text}
      </p>
      {msg.thinking && <div className="lqd-thinkbar mt-1 w-24" aria-hidden data-testid="msg-thinkbar" />}

      {msg.confirm && !msg.confirmResolved && (
        <div className="flex flex-wrap gap-2" data-testid="chat-confirm">
          <button
            type="button"
            onClick={() => onConfirm(msg.id, msg.confirm!, 'yes')}
            className="inline-flex items-center gap-1 rounded-full bg-primary px-4 py-1.5 text-[13px] font-semibold text-white hover:bg-[#163f73] transition-colors"
            data-testid="chat-confirm-yes"
          >
            Yes, do it
          </button>
          <button
            type="button"
            onClick={() => onConfirm(msg.id, msg.confirm!, 'skip')}
            className="lqd-actionpill inline-flex items-center rounded-full px-4 py-1.5 text-[13px] font-medium"
            data-testid="chat-confirm-skip"
          >
            Skip
          </button>
        </div>
      )}
      {msg.confirmResolved === 'skipped' && (
        <p className="text-[12px] text-soft italic">Skipped.</p>
      )}

      {msg.results && msg.results.length > 0 && (
        <div
          className={`space-y-1.5 max-w-[92%] ${hideInlineResults ? 'lg:hidden' : ''}`}
          data-testid="msg-results"
        >
          {msg.results.slice(0, 12).map((s) => (
            <DiamondRow
              key={stoneKey(s)}
              stone={s}
              onOpen={onOpenStone}
              blurPrice={blurPrice}
            />
          ))}
        </div>
      )}

      {msg.actions && msg.actions.length > 0 && !msg.thinking && (
        <div className="flex flex-wrap gap-1.5">
          {msg.actions.map((a, i) => (
            <button
              key={i}
              type="button"
              onClick={() => onAction(a)}
              className="lqd-actionpill inline-flex items-center gap-1 rounded-full px-3 py-1 text-[12px] font-medium"
              data-testid="msg-action"
            >
              {a.label}
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          ))}
        </div>
      )}
    </motion.div>
  )
}

function Composer({
  input, onChange, onSend, onKeyDown, sending, dictating, onToggleVoice, inputRef,
}: {
  input: string
  onChange: (v: string) => void
  onSend: () => void
  onKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void
  sending: boolean
  dictating: boolean
  onToggleVoice: () => void
  inputRef: React.RefObject<HTMLTextAreaElement | null>
}) {
  return (
    <div className="mt-3">
      <motion.div
        initial={false}
        animate={{ scale: sending ? 0.99 : 1 }}
        className="lqd-pill lqd-composer flex flex-col rounded-2xl p-2"
      >
        <div className="flex items-end gap-2">
          <button
            type="button"
            onClick={onToggleVoice}
            aria-label={dictating ? 'Stop voice' : 'Voice'}
            className={`shrink-0 inline-flex h-10 w-10 items-center justify-center rounded-xl border transition-colors ${
              dictating
                ? 'bg-danger border-transparent text-white animate-pulse'
                : 'border-[color:var(--line)] bg-white/30 text-soft hover:text-ink hover:bg-white/50'
            }`}
            data-testid="chat-mic"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
              <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
              <line x1="12" y1="19" x2="12" y2="23" />
              <line x1="8" y1="23" x2="16" y2="23" />
            </svg>
          </button>

          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={onKeyDown}
            rows={1}
            placeholder="Message DiaEx AI"
            className="flex-1 resize-none bg-transparent px-3 py-2.5 text-[15px] text-ink placeholder:text-soft focus:outline-none max-h-32"
            data-testid="chat-input"
          />

          {/* Refined icon send — hairline square disc, muted when empty, gold
              on enabled/hover. Replaces the navy gel disc. */}
          <button
            type="button"
            onClick={onSend}
            disabled={!input.trim() || sending}
            aria-label="Send"
            className="lqd-iconsend shrink-0 inline-flex h-10 w-10 items-center justify-center rounded-xl"
            data-testid="chat-send"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="19" x2="12" y2="5" />
              <polyline points="5 12 12 5 19 12" />
            </svg>
          </button>
        </div>

        {/* Thin toolbar row — mono keyboard hints, left. Keyboard-first tell. */}
        <div className="lqd-composer-hint mt-2 flex items-center gap-2.5 px-1 pt-1.5">
          <span className="inline-flex items-center gap-1">
            <span className="lqd-kbd">⏎</span> send
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="lqd-kbd">⇧⏎</span> newline
          </span>
        </div>
      </motion.div>
      <p className="lqd-mono mt-2 text-[10px] text-soft text-center tracking-wide">
        DiaEx AI · beta · sign in for order + payment info
      </p>
    </div>
  )
}
